import { PictogramKey } from "./dotPictograms";

/**
 * How a design's diamond is filled. DOT labels are not all one colour:
 * class 8 is white over black, class 7 is yellow over white, class 4.1 is
 * red-and-white vertical stripes. A solid stand-in for any of these is a
 * WRONG DOT marking on a document a customer may treat as compliant, so the
 * shape carries the real background instead of collapsing to one colour.
 */
export type HazardLabelBackground =
  | { kind: "SOLID"; color: string }
  /** Upper half over lower half, split on the diamond's horizontal diagonal. */
  | { kind: "SPLIT"; topColor: string; bottomColor: string }
  /** Vertical stripes -- HAZARD_LABEL_STRIPE_COUNT stripes of `stripeColor` on
   * `groundColor`. */
  | { kind: "STRIPED"; stripeColor: string; groundColor: string };

/**
 * The DOT design for one hazard class: its background, the colour the class
 * number is set in, and the symbol from `dotPictograms.ts` that goes in the
 * diamond's upper half.
 *
 * `pictogram` is absent on the division-unknown entries ("1", "2", "4", "5",
 * "6"), where the division decides the symbol, and on class 1, whose
 * EXPLOSIVES designs this label does not draw. An entry with no symbol
 * prints no diamond.
 */
export interface HazardLabelDesign {
  background: HazardLabelBackground;
  /** The class number's colour. Follows the design convention -- dark text
   * on light labels, white on saturated ones -- and on a split design the
   * colour readable against the LOWER half, where the number sits. */
  textColor: string;
  pictogram?: PictogramKey;
  /** The symbol's colour where it differs from `textColor`. On a split
   * design the symbol is in the upper half and the number in the lower, so
   * the two need different colours. Defaults to `textColor`. */
  pictogramColor?: string;
  /**
   * Draw the symbol across the diamond's whole upper half instead of in the
   * small centred symbol slot.
   *
   * Class 9 only. Its "symbol" is the seven vertical bars, and on a real
   * design they ARE the upper half, edge to edge -- not a device sitting in
   * the middle of it. Rendered in the ordinary slot they came out as a small
   * graphic that read as decoration rather than as the class 9 marking.
   */
  pictogramSpansUpperHalf?: boolean;

  /** Class 9 only: its class number is underlined. No other class is. */
  underlineNumber?: boolean;
}

export type HazardClassDiamondEntry = {
  name: string;
  design: HazardLabelDesign;
  /**
   * What the lower corner of the label prints. The label figures in 49 CFR
   * 172.411-172.448 show the CLASS number there ("2" on POISON GAS, "4" on
   * FLAMMABLE SOLID, "6" on POISON), not the division; only class 5 labels
   * show the division (5.1, 5.2). The division still prints in the text line
   * under the diamond.
   */
  cornerNumber: string;
};

/**
 * The design's geometry, as ratios of the diamond's own rendered size.
 *
 * Shared because both renderers draw the same design and previously drifted
 * whenever one of them was tuned alone -- the preview's job is to show what
 * will print. pdf-generator multiplies these by PDF points, Oak by CSS
 * pixels.
 */
export const HAZARD_LABEL_CORNER_RADIUS_RATIO = 0.12;
export const HAZARD_LABEL_SYMBOL_SIZE_RATIO = 0.3;
/** The symbol's centre, as a fraction of the diamond's height from its top.
 * Puts it in the upper half, clear of both the top point and the number. */
export const HAZARD_LABEL_SYMBOL_CENTER_RATIO = 0.32;
/** The class number's top edge, as a fraction of the diamond's height. */
export const HAZARD_LABEL_NUMBER_TOP_RATIO = 0.62;
export const HAZARD_LABEL_STRIPE_COUNT = 7;
/** How much wider than tall the centred symbol slot is. The symbols are not
 * square -- the corrosion symbol is over 2:1, the gas cylinder over 3:1 -- and
 * each is fitted inside this box preserving its own aspect. */
export const HAZARD_LABEL_SYMBOL_WIDTH_RATIO = 1.6;

/** For `pictogramSpansUpperHalf`: the symbol's width as a fraction of the
 * diamond's, and the height of its centre. Sized to fill the upper half
 * without crossing the diamond's left and right points. */
export const HAZARD_LABEL_UPPER_HALF_WIDTH_RATIO = 0.66;
export const HAZARD_LABEL_UPPER_HALF_CENTER_RATIO = 0.34;

/** A white design on a white label is invisible without an outline. The
 * coloured labels carry none, so this applies to the white fill only. */
export const HAZARD_LABEL_WHITE_OUTLINE_RATIO = 0.02;

/** The Class 9 label underlines its class number; no other class does. The
 * rule is the width and thickness as fractions of the NUMBER's own font size,
 * so the underline tracks the digit at any label size. */
export const HAZARD_LABEL_UNDERLINE_WIDTH_RATIO = 0.62;
export const HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO = 0.08;
export const HAZARD_LABEL_UNDERLINE_GAP_RATIO = 0.06;

/**
 * 49 CFR 172.407(c) requires a solid line inner border approximately 5mm
 * inside each edge and parallel to it, on a label at least 100mm per side.
 *
 * Expressed against the diamond's BOUNDING SQUARE, which is what `size` is
 * everywhere in the renderers. A square-on-point with a 100mm side spans
 * 100 * sqrt(2) = 141.42mm point to point, so 5mm of perpendicular inset is
 * 5 / 141.42 of that span.
 */
export const HAZARD_LABEL_INNER_BORDER_INSET_RATIO = 5 / (100 * Math.SQRT2);

/** The inner border's line weight. 172.407(c) gives no width, so this is a
 * 1.5mm line at the regulated size, scaled with everything else. */
export const HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO = 1.5 / (100 * Math.SQRT2);

/**
 * How far a square-on-point's bounding square shrinks when every edge moves
 * `inset` inwards perpendicular to itself. The perpendicular distance from
 * the centre to an edge is half the span over sqrt(2), so removing `inset`
 * from it takes `inset * 2 * sqrt(2)` off the span.
 */
export function diamondSpanInsetBy(span: number, inset: number): number {
  return span - inset * 2 * Math.SQRT2;
}

const RED = "#C0392B";
const WHITE = "#FFFFFF";
const BLACK = "#000000";
const ORANGE = "#F97316";
const GREEN = "#16A34A";
const BLUE = "#2563EB";
const YELLOW = "#FACC15";

/** Divisions 1.1 to 1.6, no symbol: this label does not draw the EXPLOSIVES designs. */
function explosiveDivisions(): Record<string, HazardClassDiamondEntry> {
  const entries: Record<string, HazardClassDiamondEntry> = {};
  for (const division of ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6"]) {
    entries[division] = {
      name: "EXPLOSIVES",
      design: { background: { kind: "SOLID", color: ORANGE }, textColor: BLACK },
      cornerNumber: division,
    };
  }
  return entries;
}

/**
 * The classes and divisions 49 CFR 172.400 labels, keyed as a profile stores
 * them. The bare keys ("1", "2", "4", "5", "6") are a class whose division is
 * unknown: they carry a name for the "Class 2 Gases" text line but no
 * symbol, so no diamond prints and the label surface warns instead.
 *
 * Class 1 prints no diamond at any division: this label does not draw the
 * EXPLOSIVES designs. See `regulatedLabelProblem`.
 */
export const HAZARD_CLASS_DIAMOND: Record<string, HazardClassDiamondEntry> = {
  "1": {
    name: "EXPLOSIVES",
    design: { background: { kind: "SOLID", color: ORANGE }, textColor: BLACK },
    cornerNumber: "1",
  },
  ...explosiveDivisions(),
  "2": {
    name: "GASES",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
    cornerNumber: "2",
  },
  "2.1": {
    name: "FLAMMABLE GAS",
    design: { background: { kind: "SOLID", color: RED }, textColor: WHITE, pictogram: "FLAME" },
    cornerNumber: "2",
  },
  "2.2": {
    name: "NON-FLAMMABLE GAS",
    design: {
      background: { kind: "SOLID", color: GREEN },
      textColor: WHITE,
      pictogram: "GAS_CYLINDER",
    },
    cornerNumber: "2",
  },
  "2.3": {
    name: "POISON GAS",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK, pictogram: "SKULL" },
    cornerNumber: "2",
  },
  "3": {
    name: "FLAMMABLE LIQUID",
    design: { background: { kind: "SOLID", color: RED }, textColor: WHITE, pictogram: "FLAME" },
    cornerNumber: "3",
  },
  "4": {
    name: "FLAMMABLE SOLIDS",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
    cornerNumber: "4",
  },
  "4.1": {
    name: "FLAMMABLE SOLID",
    design: {
      background: { kind: "STRIPED", stripeColor: RED, groundColor: WHITE },
      textColor: BLACK,
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
    cornerNumber: "4",
  },
  "4.2": {
    name: "SPONTANEOUSLY COMBUSTIBLE",
    design: {
      background: { kind: "SPLIT", topColor: WHITE, bottomColor: RED },
      textColor: WHITE,
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
    cornerNumber: "4",
  },
  "4.3": {
    name: "DANGEROUS WHEN WET",
    design: { background: { kind: "SOLID", color: BLUE }, textColor: WHITE, pictogram: "FLAME" },
    cornerNumber: "4",
  },
  "5": {
    name: "OXIDIZERS AND ORGANIC PEROXIDES",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
    cornerNumber: "5",
  },
  "5.1": {
    name: "OXIDIZER",
    design: {
      background: { kind: "SOLID", color: YELLOW },
      textColor: BLACK,
      pictogram: "FLAME_OVER_CIRCLE",
    },
    cornerNumber: "5.1",
  },
  "5.2": {
    name: "ORGANIC PEROXIDE",
    design: {
      background: { kind: "SPLIT", topColor: RED, bottomColor: YELLOW },
      textColor: BLACK,
      // A plain flame, not 5.1's flame-over-circle: the oxidizer device
      // belongs to division 5.1 only.
      pictogram: "FLAME",
      pictogramColor: BLACK,
    },
    cornerNumber: "5.2",
  },
  "6": {
    name: "TOXIC OR INFECTIOUS",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK },
    cornerNumber: "6",
  },
  "6.1": {
    name: "POISON",
    design: { background: { kind: "SOLID", color: WHITE }, textColor: BLACK, pictogram: "SKULL" },
    cornerNumber: "6",
  },
  "6.2": {
    name: "INFECTIOUS SUBSTANCE",
    design: {
      background: { kind: "SOLID", color: WHITE },
      textColor: BLACK,
      // The biohazard symbol. Division 6.1 uses the skull; printing it here
      // states the wrong hazard.
      pictogram: "BIOHAZARD",
      pictogramColor: BLACK,
    },
    cornerNumber: "6",
  },
  "7": {
    name: "RADIOACTIVE",
    design: {
      background: { kind: "SPLIT", topColor: YELLOW, bottomColor: WHITE },
      textColor: BLACK,
      pictogram: "TREFOIL",
      pictogramColor: BLACK,
    },
    cornerNumber: "7",
  },
  "8": {
    name: "CORROSIVE",
    design: {
      background: { kind: "SPLIT", topColor: WHITE, bottomColor: BLACK },
      textColor: WHITE,
      pictogram: "CORROSION",
      pictogramColor: BLACK,
    },
    cornerNumber: "8",
  },
  "9": {
    name: "MISCELLANEOUS",
    design: {
      background: { kind: "SOLID", color: WHITE },
      textColor: BLACK,
      pictogram: "CLASS_9_BARS",
      pictogramColor: BLACK,
      pictogramSpansUpperHalf: true,
      underlineNumber: true,
    },
    cornerNumber: "9",
  },
};

/**
 * The classes that split into divisions, and the divisions each one takes.
 * A profile holding only the class cannot print a label, because each
 * division is a different design (or, for class 1, needs a compatibility
 * group the profile does not carry).
 */
export const DOT_HAZARD_DIVISIONS: Readonly<Record<string, readonly string[]>> = {
  "1": ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6"],
  "2": ["2.1", "2.2", "2.3"],
  "4": ["4.1", "4.2", "4.3"],
  "5": ["5.1", "5.2"],
  "6": ["6.1", "6.2"],
};

/** Every value a hazard class field may hold: a class with no divisions, or
 * a division. The bare "1", "2", "4", "5", "6" are not among them. Written
 * out in class order, because this list is shown to the user and
 * Object.keys would put the integer keys first. */
export const DOT_HAZARD_CLASSES: readonly string[] = [
  ...DOT_HAZARD_DIVISIONS["1"],
  ...DOT_HAZARD_DIVISIONS["2"],
  "3",
  ...DOT_HAZARD_DIVISIONS["4"],
  ...DOT_HAZARD_DIVISIONS["5"],
  ...DOT_HAZARD_DIVISIONS["6"],
  "7",
  "8",
  "9",
];

/** A class 1 division with its compatibility group letter: "1.4S", "1.1D". */
const LETTERED_EXPLOSIVE_CLASS = /^1\.[1-6][A-HJKLNS]$/;

export function isDotHazardClass(hazardClass: string | null | undefined): boolean {
  const trimmed = hazardClass?.trim();
  return (
    trimmed !== undefined &&
    (DOT_HAZARD_CLASSES.includes(trimmed) || LETTERED_EXPLOSIVE_CLASS.test(trimmed))
  );
}

/** Class 1: the bare "1", a division 1.1 to 1.6, or a division with its
 * compatibility group letter. */
export function isExplosiveHazardClass(hazardClass: string | null | undefined): boolean {
  const trimmed = hazardClass?.trim();
  return (
    trimmed !== undefined &&
    (trimmed === "1" ||
      DOT_HAZARD_DIVISIONS["1"].includes(trimmed) ||
      LETTERED_EXPLOSIVE_CLASS.test(trimmed))
  );
}

/** The entry for a stored hazard class, by exact key. No prefix fallback:
 * "3.1" is not a DOT class and must not print the class 3 label. A class 1
 * compatibility group letter is dropped: "1.4S" takes the "1.4" entry. */
export function diamondForHazardClass(dotHazardClass: string): HazardClassDiamondEntry | undefined {
  const trimmed = dotHazardClass.trim();
  return HAZARD_CLASS_DIAMOND[
    LETTERED_EXPLOSIVE_CLASS.test(trimmed) ? trimmed.slice(0, 3) : trimmed
  ];
}

/** The divisions a class splits into, in order, for the message that asks a
 * user to pick one. Empty for a class that needs no division. */
export function divisionsOfHazardClass(hazardClass: string | null | undefined): string[] {
  const trimmed = hazardClass?.trim();
  return trimmed === undefined ? [] : [...(DOT_HAZARD_DIVISIONS[trimmed] ?? [])];
}

/** Why this value cannot be saved as a hazard class, or undefined when it can. */
export function hazardClassProblem(hazardClass: string | null | undefined): string | undefined {
  const trimmed = hazardClass?.trim();
  if (!trimmed) {
    return undefined;
  }
  const divisions = divisionsOfHazardClass(trimmed);
  if (trimmed === "1") {
    // Not "a different symbol": class 1 prints no label at any division
    // (see regulatedLabelProblem), so the ask is only for the division.
    return `Hazard class 1 needs a division. Pick ${divisions.join(", ")}.`;
  }
  if (divisions.length > 0) {
    return `Hazard class ${trimmed} needs a division. Each one takes a different DOT symbol, so pick ${divisions.join(", ")}.`;
  }
  if (!isDotHazardClass(trimmed)) {
    return `Hazard class ${trimmed} is not a DOT hazard class. Use one of ${DOT_HAZARD_CLASSES.join(", ")}. Class 1 may add a compatibility group letter, such as 1.4S.`;
  }
  return undefined;
}

/** `hazardClassProblem` worded for the subsidiary field. One place, so the
 * form, the server and the label dialog say the same thing. */
export function subsidiaryHazardClassProblem(
  hazardClass: string | null | undefined
): string | undefined {
  // 172.411(e): the EXPLOSIVES subsidiary label may omit the division number.
  if (hazardClass?.trim() === "1") {
    return undefined;
  }
  const problem = hazardClassProblem(hazardClass);
  return problem === undefined
    ? undefined
    : `Subsidiary ${problem.charAt(0).toLowerCase()}${problem.slice(1)}`;
}

/**
 * Why a subsidiary cannot stand beside this primary, or undefined when it can.
 * Checked only once each value is itself valid. A subsidiary with no primary
 * prints nothing, because the DOT block needs the primary, and a subsidiary
 * equal to the primary would print the same label twice.
 */
export function hazardClassPairProblem(
  hazardClass: string | null | undefined,
  subsidiaryHazardClass: string | null | undefined
): string | undefined {
  const primary = hazardClass?.trim();
  const subsidiary = subsidiaryHazardClass?.trim();
  if (!subsidiary) {
    return undefined;
  }
  if (!primary) {
    return "Subsidiary hazard class needs a hazard class. Set the hazard class, or clear the subsidiary.";
  }
  if (primary === subsidiary) {
    return `Subsidiary hazard class ${subsidiary} is the same as the hazard class. A subsidiary names a second, different hazard.`;
  }
  return undefined;
}

/**
 * Why this class prints no regulated full-size label, or undefined when it
 * prints one.
 *
 * Class 7 has no generic package label. 49 CFR 172.403(b)-(c) picks between
 * RADIOACTIVE WHITE-I, YELLOW-II and YELLOW-III by the package's transport
 * index and its surface radiation level, and each carries the contents and
 * activity. None of that is on a waste profile, so the trefoil diamond alone
 * would print as a compliant label while missing everything that decides
 * which label it is.
 *
 * Class 1 takes an EXPLOSIVES label, which this label does not draw.
 */
export function regulatedLabelProblem(hazardClass: string | null | undefined): string | undefined {
  const trimmed = hazardClass?.trim();
  if (trimmed === "7") {
    return (
      "Hazard class 7 takes a RADIOACTIVE WHITE-I, YELLOW-II or YELLOW-III label, " +
      "chosen by the package's transport index and surface radiation level (49 CFR 172.403). " +
      "The label is not printed here, because a waste profile does not carry those readings."
    );
  }
  if (isExplosiveHazardClass(trimmed)) {
    return "Hazard class 1 takes an EXPLOSIVES label (49 CFR 172.411), which is not printed here. Apply one by hand.";
  }
  return undefined;
}

/**
 * Whether this class prints a regulated full-size hazard label of its own.
 * The PDF's page list and the placement instruction both read this, so an
 * instruction cannot point at a page that never prints.
 */
export function printsRegulatedHazardLabel(hazardClass: string | null | undefined): boolean {
  if (!hazardClass || regulatedLabelProblem(hazardClass)) {
    return false;
  }
  return diamondForHazardClass(hazardClass)?.design.pictogram !== undefined;
}

/** Helvetica-Bold advance widths per em, from its AFM: every digit is 556,
 * the full stop 278. The corner number is digits and at most one stop. */
const HELVETICA_BOLD_DIGIT_EM = 0.556;
const HELVETICA_BOLD_STOP_EM = 0.278;
/** Breathing room between the number and the inner border, as a fraction of
 * the width the border leaves. */
const NUMBER_FIT_MARGIN = 0.9;

/**
 * The corner number's font size: `baseNumberSize`, or less when that would
 * run the number across the inner border.
 *
 * The number's top sits at HAZARD_LABEL_NUMBER_TOP_RATIO of the diamond,
 * where the inner border is already narrowing towards the bottom point. At
 * the 4x6 sizes a two-character number ("5.1") at the base size is wider
 * than the border leaves at its baseline, so the size is solved from the
 * geometry: the width available at the number's bottom edge, which drops by
 * two for every point of font size, must hold the number's own width.
 * Shared so the PDF and the preview shrink the same number the same way.
 */
export function hazardLabelNumberSize(
  cornerNumber: string,
  size: number,
  baseNumberSize: number
): number {
  const em =
    [...cornerNumber].filter((c) => c === ".").length * HELVETICA_BOLD_STOP_EM +
    [...cornerNumber].filter((c) => c !== ".").length * HELVETICA_BOLD_DIGIT_EM;
  const innerSpan = diamondSpanInsetBy(size, size * HAZARD_LABEL_INNER_BORDER_INSET_RATIO);
  // Width across the inner diamond at the number's top edge, then minus
  // 2 * fontSize for the bottom edge: available(f) = top - 2f >= f * em.
  const widthAtTop = innerSpan - 2 * (size * HAZARD_LABEL_NUMBER_TOP_RATIO - size / 2);
  const fitted = (widthAtTop * NUMBER_FIT_MARGIN) / (em + 2);
  return Math.min(baseNumberSize, fitted);
}
